import { Box, Stack } from '@mui/material';
import { ConfirmationDialog } from '@synergy/components';
import { DocumentStatus, LocalizedString, TopicListItem } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { PaloColumn, PaloListView, PaloStatusPill } from '@synergy/components-palo';
import { PaloNextThemeProvider } from '@synergy/theme-palo';
import { PoolBanner, PoolFormattedMessage } from '@synergy/components-next';
import { tokens } from '@synergy/theme-palo';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import * as TopicsActions from './store/topics-actions';
import { TranslatedField } from '../../components/translated-field';
import { selectedLanguageSelector } from '../../../../common/selectors/selected-language-selectors';
import { topicsListSelector } from './store/topics-selector';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

export const TopicsOverview = () => {
    const dispatch = useThunkDispatch();
    const selectedLanguage = useAppSelector(selectedLanguageSelector);
    const topics = useAppSelector(topicsListSelector);
    const availableLocales = useAppSelector(state => state.supportedLocales);
    const currentLocale = useAppSelector(state => state.currentLocale);
    const [searchParams, setSearchParams] = useSearchParams();
    const [deleteModalData, setDeleteModalData] = useState<TopicListItem | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        dispatch(TopicsActions.fetchTopics(searchParams));
    }, [dispatch, searchParams]);

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    // A topic is named in every language the shop speaks; the list says which
    // ones are still missing rather than showing eight columns of the same name.
    const missingTranslations = (value: LocalizedString): string[] => availableLocales.filter(locale => !value[locale]);

    const columns: PaloColumn<TopicListItem>[] = [
        {
            key: 'title',
            type: 'name',
            head: <FormattedMessage id="app.table.column.topic" />,
            cell: topic => (
                <Stack>
                    <TranslatedField
                        value={topic.title}
                        selectedLocale={selectedLanguage}
                        fallback={`ID: ${topic.id}`}
                    />
                    <TranslatedField
                        value={topic.description}
                        selectedLocale={selectedLanguage}
                        render={value => <Box sx={{ color: tokens.color.ink3, fontSize: '11.5px' }}>{value}</Box>}
                    />
                </Stack>
            ),
        },
        {
            key: 'isTranslated',
            type: 'flag',
            head: <FormattedMessage id="app.table.column.translation" />,
            cell: topic => {
                const missing = missingTranslations(topic.title);

                return (
                    <PaloStatusPill
                        status={0 === missing.length ? DocumentStatus.Completed : DocumentStatus.Created}
                        label={
                            0 === missing.length ? (
                                <FormattedMessage id="app.common.translated" />
                            ) : (
                                <FormattedMessage
                                    id="app.common.missingTranslations"
                                    values={{ value: missing.join(', ') }}
                                />
                            )
                        }
                    />
                );
            },
        },
    ];

    const handleDeleteConfirm = () => {
        if (!deleteModalData?.id || deleteModalData.id < 0) {
            return;
        }
        dispatch(TopicsActions.deleteTopic(deleteModalData.id));
        setDeleteModalData(null);
    };

    return (
        <>
            <PaloListView<TopicListItem, number>
                embedded={true}
                columns={columns}
                items={topics.items}
                getRowId={topic => topic.id}
                emptyLabel="Keine Bedarfsthemen."
                onRowClick={topic => navigate(topic.id.toString())}
                rowActions={topic => [
                    {
                        label: <FormattedMessage id="app.common.delete" />,
                        onClick: () => setDeleteModalData(topic),
                    },
                ]}
                pagination={{
                    count: topics.total,
                    page: Number(searchParams.get('page') ?? 1),
                    limit: Number(searchParams.get('limit') ?? 50),
                    onPageChange: page => updateParams({ page: String(page) }),
                    onLimitChange: limit => updateParams({ limit: String(limit), page: '1' }),
                }}
            />

            <ConfirmationDialog
                isVisible={!!deleteModalData}
                title={<FormattedMessage id="app.configuration.topics.delete" />}
                action={<FormattedMessage id="app.common.delete" />}
                onConfirm={handleDeleteConfirm}
                onCancel={() => setDeleteModalData(null)}
            >
                <Stack gap={2}>
                    <Box>
                        <PoolFormattedMessage
                            id="app.configuration.topics.delete.text"
                            values={{ name: deleteModalData?.title[currentLocale] }}
                        />
                    </Box>
                    {deleteModalData?.isAssigned && (
                        <PaloNextThemeProvider>
                            <PoolBanner variant="outlined" sx={{ mb: 2 }} severity="warning">
                                <FormattedMessage id="app.configuration.topics.delete.warningText" />
                            </PoolBanner>
                        </PaloNextThemeProvider>
                    )}
                </Stack>
            </ConfirmationDialog>
        </>
    );
};
